import type { InfiniteQueryHook, Middleware, QueryHook } from 'react-query-kit';
import type { QueryClient } from '@tanstack/react-query';
import { CanceledError, isAxiosError } from 'axios';

import { ZepQuizApiError } from '@/api/httpClient';

const QUERY_RETRY_BUDGET_MS = 60_000;
const RETRY_DELAYS_MS = [2000, 4000, 6000, 8000, 10_000, 10_000];

type Retry =
  | boolean
  | number
  | ((failureCount: number, error: Error) => boolean);
type RetryDelay = number | ((failureCount: number, error: Error) => number);
type Budget = { startedAt: number; lastUnavailable?: Error };
// 재시도와 무한 쿼리의 페이지는 fetch 의 signal 하나를, 학급 모달의 페이지들은 검색 signal 하나를 공유한다.
const budgets = new WeakMap<AbortSignal, Budget>();
const errorDeadlines = new WeakMap<Error, number>();

const isServiceUnavailable = (error: unknown) =>
  (error instanceof ZepQuizApiError && error.httpStatus === 503) ||
  (isAxiosError(error) && error.response?.status === 503);

const runWithRetryBudget = async <T>(
  signal: AbortSignal,
  request: (signal: AbortSignal) => Promise<T>,
  budgetSignal: AbortSignal = signal,
): Promise<T> => {
  let budget = budgets.get(budgetSignal);
  if (!budget) {
    budget = { startedAt: Date.now() };
    budgets.set(budgetSignal, budget);
  }
  const deadline = budget.startedAt + QUERY_RETRY_BUDGET_MS;
  // 시계는 조회 시작부터 돌지만 마감은 503 을 받은 뒤에만 건다 — 느려도 성공하는 조회는 끊지 않는다.
  const outage = budget.lastUnavailable;
  if (outage && Date.now() >= deadline) throw outage;
  if (signal.aborted || budgetSignal.aborted) throw new CanceledError();

  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener('abort', cancel, { once: true });
  budgetSignal.addEventListener('abort', cancel, { once: true });
  const timer = outage ? setTimeout(cancel, deadline - Date.now()) : undefined;
  try {
    return await request(controller.signal);
  } catch (error) {
    if (error instanceof Error && isServiceUnavailable(error)) {
      budget.lastUnavailable = error;
      errorDeadlines.set(error, deadline);
    }
    if (!signal.aborted && budget.lastUnavailable && Date.now() >= deadline) {
      throw budget.lastUnavailable;
    }
    throw error;
  } finally {
    controller.abort();
    clearTimeout(timer);
    signal.removeEventListener('abort', cancel);
    budgetSignal.removeEventListener('abort', cancel);
  }
};

/** 503 이 아닌 오류는 주어진 retry·retryDelay 를 따르고, 값이 없으면 TanStack 기본값을 쓴다. */
const withRetryPolicy = ({
  retry,
  retryDelay,
}: {
  retry?: Retry;
  retryDelay?: RetryDelay;
}) => ({
  retry: (failureCount: number, error: Error) => {
    const deadline = errorDeadlines.get(error);
    if (deadline !== undefined) {
      return failureCount < RETRY_DELAYS_MS.length && Date.now() < deadline;
    }
    if (typeof retry === 'function') return retry(failureCount, error);
    return typeof retry === 'number'
      ? failureCount < retry
      : retry ?? failureCount < 3;
  },
  retryDelay: (attemptIndex: number, error: Error) => {
    const deadline = errorDeadlines.get(error);
    if (deadline === undefined) {
      if (typeof retryDelay === 'function')
        return retryDelay(attemptIndex, error);
      return retryDelay ?? Math.min(1000 * 2 ** attemptIndex, 30_000);
    }
    return Math.max(
      0,
      Math.min(
        (RETRY_DELAYS_MS[attemptIndex] ?? 10_000) + Math.random() * 500,
        deadline - Date.now(),
      ),
    );
  },
});

// 옵션·콜백 인자·결과는 hook 종류(일반·무한 쿼리)마다 달라 타입 인자로 받는다.
type RetryableOptions<
  TContext extends { signal: AbortSignal },
  TQuery extends { state: { data?: unknown } },
> = {
  fetcher: (variables: never, context: TContext) => unknown;
  retry?: Retry;
  retryDelay?: RetryDelay;
  throwOnError?: boolean | ((error: Error, query: TQuery) => boolean);
};
type HookResult = { isError: boolean; error: unknown; data?: unknown };

/**
 * Atlas Search 를 거치는 react-query-kit 조회에 붙인다 (`use: [retryOnServiceUnavailable]`).
 * 503 은 2·4·6·8·10·10초 간격으로 조회 시작부터 60초 안에서 다시 시도하고, 그 밖의 오류는 기존
 * retry 를 따른다. 이미 보여 준 데이터가 있으면 503 소진을 화면 오류로 올리지 않고 목록을 지킨다.
 *
 * 미들웨어가 받는 options 에는 QueryClient 기본값이 이미 합쳐져 있어 앱 기본 retry 를 그대로 물려받는다.
 */
export function retryOnServiceUnavailable<
  TFnData,
  TVariables,
  TError,
  TPageParam,
>(
  useNext: Parameters<
    Middleware<InfiniteQueryHook<TFnData, TVariables, TError, TPageParam>>
  >[0],
): ReturnType<
  Middleware<InfiniteQueryHook<TFnData, TVariables, TError, TPageParam>>
>;
export function retryOnServiceUnavailable<TFnData, TVariables, TError>(
  useNext: Parameters<Middleware<QueryHook<TFnData, TVariables, TError>>>[0],
): ReturnType<Middleware<QueryHook<TFnData, TVariables, TError>>>;
export function retryOnServiceUnavailable<
  TContext extends { signal: AbortSignal },
  TQuery extends { state: { data?: unknown } },
  TOptions extends RetryableOptions<TContext, TQuery>,
  TResult extends HookResult,
>(
  useNext: (options: TOptions, queryClient?: QueryClient) => TResult,
): (options: TOptions, queryClient?: QueryClient) => TResult {
  return (options, queryClient) => {
    const { fetcher, throwOnError } = options;
    const keepsData = (error: unknown, data: unknown) =>
      isServiceUnavailable(error) && data !== undefined;
    const result = useNext(
      {
        ...options,
        ...withRetryPolicy(options),
        fetcher: (variables: never, context: TContext) =>
          runWithRetryBudget(context.signal, async signal =>
            fetcher(variables, { ...context, signal }),
          ),
        throwOnError: (error: Error, query: TQuery) =>
          !keepsData(error, query.state.data) &&
          (typeof throwOnError === 'function'
            ? throwOnError(error, query)
            : Boolean(throwOnError)),
      },
      queryClient,
    );
    return {
      ...result,
      isError: result.isError && !keepsData(result.error, result.data),
    };
  };
}

/**
 * react-query-kit 밖의 `useQuery`·`fetchQuery` 용. 503 재시도만 더한다 — 데이터 보존이 필요한 목록은
 * `retryOnServiceUnavailable` 을 쓴다. 결과를 쿼리 옵션에 펼친다: `useQuery({ ...withServiceUnavailableRetry({ queryFn, retry }), queryKey })`.
 * `budgetSignal` 을 주면 그 signal 을 공유하는 조회가 한 예산을 나눠 쓴다.
 */
export const withServiceUnavailableRetry = <
  TContext extends { signal: AbortSignal },
  TData,
>(
  {
    queryFn,
    retry,
    retryDelay,
  }: {
    queryFn: (context: TContext) => Promise<TData>;
    retry?: Retry;
    retryDelay?: RetryDelay;
  },
  budgetSignal?: AbortSignal,
) => ({
  ...withRetryPolicy({ retry, retryDelay }),
  // NoInfer: 펼쳐 넣은 useQuery 의 queryKey 타입이 TContext 로 역추론되면 never 가 된다
  queryFn: (context: NoInfer<TContext>) =>
    runWithRetryBudget(
      context.signal,
      signal => queryFn({ ...context, signal }),
      budgetSignal,
    ),
});
